import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useState, type ReactNode } from 'react'

import { ApiError } from '@/shared/api'
import { env } from '@/shared/config/env'

/**
 * Configuración global de TanStack Query.
 *
 * Estos valores aplican a TODAS las consultas del sistema. Cada hook puede
 * sobrescribirlos si lo necesita (como hace `useCustomers` con su staleTime).
 * Tenerlos aquí evita que cada desarrollador invente los suyos.
 */
function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        /**
         * Cuánto tiempo se considera "fresco" un dato antes de volver a
         * pedirlo. Un minuto es un punto medio razonable para un panel
         * administrativo.
         */
        staleTime: 60 * 1000,

        /**
         * Reintentos ante fallo. La lógica importa: reintentar un 404 o un 403
         * es inútil (la respuesta será la misma) y solo hace esperar al usuario
         * sin razón. Solo se reintenta lo que puede ser transitorio: fallos de
         * red y errores 5xx del servidor.
         */
        retry: (failureCount, error) => {
          if (error instanceof ApiError && !error.isNetworkError && !error.isServerError) {
            return false
          }
          return failureCount < 2
        },

        /**
         * Por defecto, TanStack Query recarga cada vez que la ventana recupera
         * el foco. En un panel donde la gente alterna entre pestañas todo el
         * día, eso son peticiones constantes sin valor. Se desactiva; las
         * pantallas que necesiten datos siempre frescos (caja) lo activan.
         */
        refetchOnWindowFocus: false,
      },

      mutations: {
        // Las mutaciones NO se reintentan solas: reintentar un "registrar pago"
        // podría cobrarle dos veces al cliente. Ante la duda, que decida el
        // usuario pulsando otra vez.
        retry: false,
      },
    },
  })
}

/**
 * Provee el cliente de consultas a toda la aplicación.
 *
 * El `useState` con función inicial crea el QueryClient UNA sola vez. Si se
 * escribiera `new QueryClient()` directamente en el cuerpo del componente, se
 * crearía uno nuevo en cada render y se perdería toda la caché — un error
 * silencioso y difícil de detectar, porque la app "funciona", solo que lenta.
 */
export function QueryProvider({ children }: { children: ReactNode }) {
  const [queryClient] = useState(createQueryClient)

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* Panel de depuración: muestra el estado de la caché, qué consultas
          están activas y qué datos tienen. Solo en desarrollo. */}
      {env.VITE_ENABLE_DEVTOOLS && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  )
}
